import { useState, useEffect, useLayoutEffect, useRef, useContext } from 'react'
import { X, Tags, AlertCircle } from 'lucide-react'
import { BuilderContext } from './BuilderContextInstance'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'
import { TokenChips } from './ModelPathField'
import { interpolateMetaText } from './modelPath'

/**
 * Panneau « Métadonnées » — les balises que servent les moteurs de recherche et
 * les aperçus de partage pour CETTE page.
 *
 * Elles sont persistées en JSON dans `field_meta_tags` (un blob, comme
 * `modelFor` : c'est du contenu, pas de l'infra générique à configurer), et
 * c'est `SitePublicController::shell` (module orbit_site) qui les injecte dans
 * le `<head>` du HTML servi — les crawlers d'aperçu n'exécutent pas de JS, une
 * mise à jour côté client ne les atteindrait pas.
 *
 * Tout est facultatif : un champ vide reprend le comportement par défaut
 * (titre composé « titre de page — nom du site », URL canonique = l'URL
 * demandée). Le contrôle de forme est refait côté Drupal (`validateMeta()`).
 *
 * Exception assumée à la règle « édition en place » du builder : ces balises ne
 * s'affichent nulle part dans la page, il n'y a donc rien à éditer en place.
 *
 * TROIS PORTEURS, un seul panneau. Une page n'est pas la seule chose que le
 * builder sert à une URL publique :
 *
 *  - une PAGE, à son URI ;
 *  - un CONTENU, à travers le modèle de son type (`/actualites/mon-article`) ;
 *  - le MODÈLE lui-même, qui n'est servi nulle part mais dont les balises sont
 *    le DÉFAUT de tous les contenus du type (`readMeta($instance) +
 *    readMeta($pageNode)`, cf. OrbitBuilderController) — celles du contenu
 *    l'emportent, champ par champ.
 *
 * Le panneau prend donc, en option, la valeur et le « Appliquer » de son
 * appelant (`value` / `onApply`) ; sans eux il garde le comportement d'avant et
 * écrit dans l'état de la page — ce que fait aussi le modèle, dont les balises
 * sont bien les siennes. `heading`, `intro` et `urlPreview` disent lequel des
 * trois on édite. Même formulaire, mêmes règles, même aperçu : un éditeur n'a
 * pas trois grammaires à apprendre.
 */
export const BuilderMetaPanel = ({
  onClose,
  value = null,
  onApply = null,
  heading = null,
  intro = null,
  titleFallback = null,
  urlPreview = null,
  tokens = null,
}) => {
  const scrim = useScrimDismiss(onClose)
  // `useContext` et non `useBuilder` : ouvert sur un CONTENU dont le type n'a
  // pas encore de modèle, le panneau rend hors de tout BuilderProvider — et
  // `useBuilder` y lèverait. Le contexte n'est lu que par le mode « page ».
  const builder = useContext(BuilderContext)
  const controlled = typeof onApply === 'function'
  const page = builder?.page ?? {}

  const [meta, setMeta] = useState(() => ({ ...EMPTY_META, ...((controlled ? value : page.meta) || {}) }))
  const firstFieldRef = useRef(null)
  // Les champs de texte, par clé : une pastille `@champ` s'insère dans CELUI
  // qu'on est en train d'écrire, à l'endroit du curseur.
  const inputs = useRef({})
  const [activeKey, setActiveKey] = useState('title')
  const registerInput = (key) => (el) => {
    inputs.current[key] = el
    if (key === 'title') firstFieldRef.current = el
  }

  useEffect(() => { firstFieldRef.current?.focus() }, [])

  useEscapeClose(onClose)

  const set = (key, value) => setMeta(prev => ({ ...prev, [key]: value }))

  /** Insère `@clé` dans le champ visé, là où est le curseur. */
  const insertToken = (fieldKey) => {
    const target = TOKENABLE.includes(activeKey) ? activeKey : 'title'
    const input = inputs.current[target]
    const token = `@${fieldKey}`
    const current = meta[target] ?? ''
    if (!input) {
      set(target, `${current}${token}`)
      return
    }
    const start = input.selectionStart ?? current.length
    const end = input.selectionEnd ?? current.length
    set(target, current.slice(0, start) + token + current.slice(end))
    pendingCaret.current = { key: target, pos: start + token.length }
  }

  // Où replacer le curseur au prochain rendu. En `useLayoutEffect` et non dans
  // un `requestAnimationFrame` : l'animation frame arrive APRÈS le commit de
  // React, donc parfois après les premières frappes suivantes — le curseur
  // sautait en plein mot (« @titleerya — Em » au lieu de « @title — Emerya »).
  const pendingCaret = useRef(null)
  useLayoutEffect(() => {
    const pending = pendingCaret.current
    if (!pending) return
    pendingCaret.current = null
    const input = inputs.current[pending.key]
    if (!input) return
    input.focus()
    input.setSelectionRange(pending.pos, pending.pos)
  })

  const errors = validate(meta)
  const hasErrors = Object.keys(errors).length > 0

  const handleApply = () => {
    if (hasErrors) return
    // On ne persiste que ce qui est renseigné : un `{}` fait retomber la page
    // sur les défauts au lieu de figer des chaînes vides dans le champ.
    const cleaned = Object.fromEntries(
      Object.entries(meta)
        .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
        .filter(([, v]) => v !== '' && v != null),
    )
    if (controlled) {
      onApply(cleaned)
    } else {
      builder.setPage(prev => ({ ...prev, meta: cleaned }))
      builder.setIsDirty(true)
    }
    onClose()
  }

  // Le repère d'un contenu ou d'un modèle est passé par l'appelant ; celui
  // d'une page se lit dans son état. Un `titleFallback` / `urlPreview`
  // explicite prime donc, contrôlé ou non : c'est lui qui distingue les trois
  // porteurs à l'écran.
  const fallbackTitle = titleFallback || (controlled ? null : page.title)
  // Un GABARIT ne se lit pas tel quel : l'aperçu remplit ses jetons avec le
  // libellé de chaque champ, pour montrer la phrase que verra un moteur plutôt
  // que le « @title — Emerya » qu'on vient d'écrire.
  const sample = tokens
    ? Object.fromEntries(tokens.map(t => [t.key, t.label || t.key]))
    : null
  const resolve = (text) => (sample ? interpolateMetaText(text, sample) || text : text)
  const previewTitle = resolve(meta.title.trim()) || fallbackTitle || 'Titre de la page'
  const previewDescription = resolve(meta.description.trim())
  const previewUrl = urlPreview || (controlled ? '/' : `/${page.slug || 'ma-page'}`)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4" {...scrim}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="builder-meta-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tags size={16} className="text-teal-700" />
            <h2 id="builder-meta-title" className="text-base font-semibold text-gray-800">
              {heading || 'Métadonnées de la page'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Fermer">
            <X size={18} />
          </button>
        </header>

        <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50 text-xs text-gray-500">
          {intro || (
            <>
              Ces balises alimentent le référencement et les aperçus de partage
              (LinkedIn, Slack, WhatsApp…). Laissées vides, elles reprennent leurs
              valeurs par défaut : le titre de la page suivi du nom du site.
            </>
          )}
        </div>

        <main className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Les champs du type, citables dans TOUTES les balises. Une seule
              rangée, qui vise le champ où l'on écrit : trois rangées (titre,
              description, image) diraient trois fois la même chose. */}
          {tokens && tokens.length > 0 && (
            <div className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2">
              <TokenChips
                fields={tokens}
                onInsert={insertToken}
                label="Champs du contenu citables dans les balises"
                hint={
                  <>
                    Chaque <code className="font-mono">@champ</code> met la valeur du champ
                    du contenu affiché : <span className="font-mono">@{tokens[0].key} — Nom du site</span>{' '}
                    donne un titre par contenu. Le texte est repris tel quel (le balisage
                    est retiré) ; une balise dont aucun champ n&apos;a répondu retombe sur
                    son défaut.
                  </>
                }
              />
            </div>
          )}
          <Field
            id="meta-title"
            ref={registerInput('title')}
            onFocus={() => setActiveKey('title')}
            label="Titre (balise title)"
            value={meta.title}
            onChange={v => set('title', v)}
            placeholder={fallbackTitle || 'Titre affiché dans l’onglet et les résultats'}
            hint="Remplace le titre composé. 60 caractères environ avant troncature dans les résultats."
            counter={{ current: meta.title.length, ideal: 60, max: 255 }}
            error={errors.title}
          />

          <Field
            id="meta-description"
            as="textarea"
            ref={registerInput('description')}
            onFocus={() => setActiveKey('description')}
            label="Description"
            value={meta.description}
            onChange={v => set('description', v)}
            placeholder="Résumé de la page en une ou deux phrases."
            hint="Affichée sous le titre dans les résultats de recherche. 160 caractères environ."
            counter={{ current: meta.description.length, ideal: 160, max: 500 }}
            error={errors.description}
          />

          <Field
            id="meta-image"
            ref={registerInput('image')}
            onFocus={() => setActiveKey('image')}
            label="Image de partage"
            value={meta.image}
            onChange={v => set('image', v)}
            placeholder="/sites/default/files/… ou https://…"
            hint="Image affichée quand le lien est partagé. Format 1200×630 recommandé."
            mono
            error={errors.image}
          />
          {/* Un gabarit (`@cover`) n'est pas une URL : rien à prévisualiser
              tant qu'il n'est pas rempli par un contenu. */}
          {meta.image.trim() !== '' && !errors.image && !hasToken(meta.image) && (
            <img
              src={meta.image.trim()}
              alt=""
              className="max-h-32 rounded-lg border border-gray-200 object-cover"
            />
          )}

          <div>
            <label htmlFor="meta-robots" className="block text-xs font-medium text-gray-500 mb-1.5">
              Indexation
            </label>
            <select
              id="meta-robots"
              value={meta.robots || 'index'}
              onChange={e => set('robots', e.target.value === 'index' ? '' : e.target.value)}
              aria-describedby="meta-robots-help"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600"
            >
              <option value="index">Indexable par les moteurs de recherche</option>
              <option value="noindex">Ne pas indexer (noindex)</option>
            </select>
            <p id="meta-robots-help" className="text-xs text-gray-400 mt-1.5">
              « Ne pas indexer » convient à une page de remerciement ou à une landing
              de campagne. Le <span className="font-mono">robots.txt</span> du site, lui,
              se règle sur la fiche du site.
            </p>
          </div>

          <Field
            id="meta-canonical"
            ref={registerInput('canonical')}
            onFocus={() => setActiveKey('canonical')}
            label="URL canonique"
            value={meta.canonical}
            onChange={v => set('canonical', v)}
            placeholder="https://exemple.com/page-de-reference"
            hint="À renseigner seulement si le même contenu fait référence ailleurs. Vide = l’URL de la page."
            mono
            error={errors.canonical}
          />

          {/* Aperçu — ce que verra un moteur de recherche. */}
          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/40">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Aperçu</p>
            <p className="text-xs text-gray-500 font-mono truncate">{previewUrl}</p>
            <p className="text-base text-blue-800 truncate">{previewTitle}</p>
            <p className="text-xs text-gray-600 line-clamp-2">
              {previewDescription || 'Aucune description : les moteurs en composeront une depuis le contenu de la page.'}
            </p>
          </div>
        </main>

        <footer className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {hasErrors ? 'Erreurs à corriger' : 'Tous les champs sont facultatifs'}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={hasErrors}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Appliquer
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

const EMPTY_META = { title: '', description: '', image: '', canonical: '', robots: '' }

/** Les balises où une pastille `@champ` a un sens — `robots` est une liste. */
const TOKENABLE = ['title', 'description', 'image', 'canonical']

/** La valeur cite-t-elle un champ ? Alors c'est un gabarit, pas un texte fini. */
const hasToken = (value) => /@[a-zA-Z_][a-zA-Z0-9_]*/.test(String(value || ''))

/**
 * Un champ texte du panneau : libellé lié, aide et compteur décrits par
 * `aria-describedby`, erreur annoncée dans le même flux.
 */
const Field = ({ id, label, value, onChange, onFocus, placeholder, hint, counter, error, mono, as, ref }) => {
  const Tag = as === 'textarea' ? 'textarea' : 'input'
  const describedBy = [hint ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean).join(' ')
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-gray-500 mb-1.5">{label}</label>
      <Tag
        id={id}
        ref={ref}
        type={as === 'textarea' ? undefined : 'text'}
        rows={as === 'textarea' ? 3 : undefined}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        placeholder={placeholder}
        aria-describedby={describedBy || undefined}
        aria-invalid={!!error}
        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 ${
          mono ? 'font-mono' : ''
        } ${error ? 'border-red-300' : 'border-gray-200'}`}
      />
      <div className="flex items-start justify-between gap-3 mt-1.5">
        {hint && <p id={`${id}-help`} className="text-xs text-gray-400">{hint}</p>}
        {counter && (
          <span className={`text-xs shrink-0 tabular-nums ${counter.current > counter.ideal ? 'text-amber-700' : 'text-gray-400'}`}>
            {counter.current}/{counter.ideal}
          </span>
        )}
      </div>
      {error && (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600 flex items-center gap-1.5">
          <AlertCircle size={12} /> {error}
        </p>
      )}
    </div>
  )
}

/**
 * Mêmes règles que `OrbitBuilderController::validateMeta()` — le front les
 * rejoue pour dire l'erreur tout de suite, Drupal reste l'arbitre.
 */
function validate(meta) {
  const errors = {}
  if (meta.title.length > 255) errors.title = '255 caractères au maximum.'
  if (meta.description.length > 500) errors.description = '500 caractères au maximum.'
  for (const key of ['image', 'canonical']) {
    const url = meta[key].trim()
    // Une valeur qui cite un champ est un GABARIT : son URL n'existera qu'une
    // fois remplie par un contenu, il n'y a rien à contrôler ici. Même règle
    // côté Drupal (`validateMeta()`), qui reste l'arbitre.
    if (url === '' || hasToken(url)) continue
    if (url[0] !== '/' && !/^https?:\/\//i.test(url)) {
      errors[key] = 'Une URL http(s), ou un chemin commençant par /.'
    }
  }
  return errors
}

export default BuilderMetaPanel
